import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { HStack } from '~/components/elements/HStack'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeGroupSelector } from '~/components/features/CustomerAttributeGroupSelector'
import { QuestionTreeSelector } from '~/components/features/QuestionTreeSelector'
import { useFormField } from '~/hooks/useFormField'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery'

import { useAvailableCustomerAttributeGroups } from '../../hooks/useAvailableCustomerAttributeGroups'

import { type InputValues } from './utils'

export type FilterInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const FilterInput = ({ index, onDelete }: FilterInputProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const categoryField = useFormField(control, `filters.${index}.category`)
  const idField = useFormField(control, `filters.${index}.id`)

  const questions = useSurveyQueryQuestions()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups(undefined)

  const handleDeleteButtonClick = React.useCallback<
    EH<IconButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  return (
    <VStack border="light" padding={2} spacing={2}>
      <HStack align="center" justify="space-between" spacing={2}>
        {match(categoryField.value)
          .with('Question', () => (
            <Typography variant="normal">{t('questionnaire')}</Typography>
          ))
          .with('CustomerAttribute', () => (
            <Typography variant="normal">
              {t('customized_segments_group')}
            </Typography>
          ))
          .exhaustive()}

        <IconButton
          buttonStyle="light"
          fillMode="flat"
          icon="delete"
          onClick={handleDeleteButtonClick}
        />
      </HStack>

      {match(categoryField.value)
        .with('Question', () => (
          <QuestionTreeSelector questions={questions} {...idField} />
        ))
        .with('CustomerAttribute', () => (
          <CustomerAttributeGroupSelector
            customerAttributeGroups={customerAttributeGroups}
            {...idField}
          />
        ))
        .exhaustive()}
    </VStack>
  )
}
